import { faCcVisa } from "@fortawesome/free-brands-svg-icons";
import {
  faCoins,
  faWallet,
  faUser,
  faShoppingBasket
} from "@fortawesome/pro-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useRef } from "react";

import { T } from "@repo/transifex";
import type { PaymentProvider } from "@repo/types";
import { Button } from "~/components";
import { MobilePayIcon } from "~/components/MobilePayIcon";
import { SwishIcon } from "~/components/SwishIcon";
import { useAppContext } from "~/providers/app";
import { useOrderLinesToCollect } from "~/providers/store/collect/hooks";
import { getTerminalId } from "~/utils/tills/get-terminal-id";

import {
  inferPaymentButtons,
  type InferPaymentButtonsArgs
} from "./infer-payment-buttons";
import { PaymentTerminalButton } from "./payment-terminal-button";

type PaymentButtonsProps = {
  isSubmitDisabled: boolean;
  onCreate: (paymentProvider: PaymentProvider) => void;
} & Omit<InferPaymentButtonsArgs, "hasCashDrawer">;

export const PaymentButtons = ({
  isSubmitDisabled,
  onCreate,
  till,
  unitPriceBalance,
  unitPriceCollectAvailableInclVat,
  unitPriceBasketTotalInclVat,
  unitPriceBalanceToPayInclVat,
  isBasketEmpty,
  isSplitPaymentActive,
  isUserAuthenticated
}: PaymentButtonsProps) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const terminalId = getTerminalId(till);

  const {
    hardware: { printer }
  } = useAppContext();

  const orderLinesToCollect = useOrderLinesToCollect();
  const isCollectionActive = orderLinesToCollect.length > 0;

  const lastClickAtRef = useRef(0);

  const paymentButtons = useMemo(
    () =>
      inferPaymentButtons({
        till,
        unitPriceBasketTotalInclVat,
        unitPriceBalanceToPayInclVat,
        unitPriceBalance,
        unitPriceCollectAvailableInclVat,
        isBasketEmpty,
        isSplitPaymentActive,
        isUserAuthenticated,
        hasCashDrawer: printer?.hasCashDrawer ?? false
      }),
    [
      till,
      unitPriceBasketTotalInclVat,
      unitPriceBalanceToPayInclVat,
      unitPriceBalance,
      unitPriceCollectAvailableInclVat,
      isBasketEmpty,
      isSplitPaymentActive,
      isUserAuthenticated,
      printer?.hasCashDrawer
    ]
  );

  if (paymentButtons.type === "none") {
    return (
      <div className="flex">
        <Button
          textClassName="whitespace-normal break-words"
          className="min-w-0 flex-1 rounded-none"
          disabled
          icon={faUser}
          size="large"
          variant="secondary"
        >
          <T _str="Authentication required" />
        </Button>
      </div>
    );
  }

  if (paymentButtons.type === "empty-basket") {
    return (
      <div className="flex">
        <Button
          textClassName="whitespace-normal break-words"
          className="min-w-0 flex-1 rounded-none"
          disabled
          icon={faShoppingBasket}
          size="large"
          variant="secondary"
        >
          <T _str="Empty basket" />
        </Button>
      </div>
    );
  }

  const handleOnClick = (paymentProvider: PaymentProvider) => {
    const now = Date.now();
    if (now - lastClickAtRef.current < 1000) return;
    lastClickAtRef.current = now;

    onCreate(paymentProvider);
    navigate({
      pathname: `${pathname}/${
        paymentProvider === "mobilepay_own" ? "mobilepay" : paymentProvider
      }`,
      search
    });
  };

  if (
    paymentButtons.type === "single" &&
    paymentButtons.paymentOption === "billing"
  ) {
    return (
      <div className="flex">
        <Button
          textClassName="whitespace-normal break-words"
          className="min-w-0 flex-1 rounded-none"
          disabled={isSubmitDisabled}
          onClick={() => {
            handleOnClick("billing");
          }}
          size="large"
          variant="primary"
        >
          <T _str="Complete purchases" />
        </Button>
      </div>
    );
  }

  if (
    paymentButtons.type === "single" &&
    paymentButtons.paymentOption === "credit"
  ) {
    return (
      <div className="flex">
        <Button
          textClassName="whitespace-normal break-words"
          className="min-w-0 flex-1 rounded-none"
          disabled={isSubmitDisabled}
          onClick={() => {
            handleOnClick("credit");
          }}
          icon={faWallet}
          size="large"
          variant="primary"
        >
          {isCollectionActive ? (
            <T _str="Collect & Pay with balance" />
          ) : (
            <T _str="Pay with balance" />
          )}
        </Button>
      </div>
    );
  }

  if (
    paymentButtons.type === "single" &&
    paymentButtons.paymentOption === "zero_payment"
  ) {
    return (
      <div className="flex">
        <Button
          textClassName="whitespace-normal break-words"
          className="min-w-0 flex-1 rounded-none"
          disabled={isSubmitDisabled}
          onClick={() => {
            handleOnClick("credit");
          }}
          size="large"
          variant="primary"
        >
          <T _str="Complete order" />
        </Button>
      </div>
    );
  }

  if (paymentButtons.type === "insufficient-balance") {
    return (
      <div className="flex">
        <Button
          textClassName="whitespace-normal break-words"
          className="min-w-0 flex-1 rounded-none"
          disabled
          icon={faWallet}
          size="large"
          variant="danger-light"
        >
          <T _str="Insufficient balance" />
        </Button>
      </div>
    );
  }

  if (paymentButtons.type === "multiple") {
    return (
      <div className="flex flex-col">
        <div className="flex w-full overflow-hidden">
          <Button
            textClassName="text-xs xl:text-sm whitespace-normal break-words leading-tight"
            className="min-w-0 flex-1 overflow-hidden rounded-none"
            disabled={isSubmitDisabled}
            hidden={!paymentButtons.paymentOptions.includes("mobilepay_own")}
            icon={<MobilePayIcon />}
            onClick={() => {
              handleOnClick("mobilepay_own");
            }}
            size="large"
            variant="info-inverted"
          >
            {isCollectionActive ? (
              <T _str="Collect & Pay with {mobilePay}" mobilePay="MobilePay" />
            ) : (
              <T _str="Pay with {mobilePay}" mobilePay="MobilePay" />
            )}
          </Button>

          <PaymentTerminalButton
            isTerminalConnected={Boolean(terminalId)}
            hidden={!paymentButtons.paymentOptions.includes("swish")}
          >
            <Button
              textClassName="text-xs xl:text-sm whitespace-normal break-words leading-tight"
              className="min-w-0 flex-1 overflow-hidden rounded-none bg-orange-200 text-orange-500"
              disabled={isSubmitDisabled}
              icon={<SwishIcon />}
              onClick={() => {
                handleOnClick("swish");
              }}
              size="large"
              variant="info-inverted"
            >
              {isCollectionActive ? (
                <T _str="Collect & Pay with {swish}" swish="Swish" />
              ) : (
                <T _str="Pay with {swish}" swish="Swish" />
              )}
            </Button>
          </PaymentTerminalButton>

          <Button
            textClassName="text-xs xl:text-sm whitespace-normal break-words leading-tight"
            className="min-w-0 flex-1 overflow-hidden rounded-none"
            disabled={isSubmitDisabled}
            hidden={!paymentButtons.paymentOptions.includes("cash")}
            icon={faCoins}
            onClick={() => {
              handleOnClick("cash");
            }}
            size="large"
            variant="secondary"
          >
            {isCollectionActive ? (
              <T _str="Collect & Pay with cash" />
            ) : (
              <T _str="Pay with cash" />
            )}
          </Button>
        </div>

        <PaymentTerminalButton
          isTerminalConnected={Boolean(terminalId)}
          hidden={!paymentButtons.paymentOptions.includes("card")}
        >
          <Button
            textClassName="whitespace-normal break-words"
            className="min-w-0 flex-1 rounded-none"
            disabled={
              isSubmitDisabled ||
              (isSplitPaymentActive &&
                till?.paymentGatewayConfig?.provider === "freedompay")
            }
            icon={faCcVisa}
            onClick={() => {
              handleOnClick("card");
            }}
            size="large"
          >
            {isCollectionActive ? (
              <T _str="Collect & Pay with card" />
            ) : (
              <T _str="Pay with card" />
            )}
          </Button>
        </PaymentTerminalButton>
      </div>
    );
  }

  return null;
};
